/**
 * Upcoming supplier payments grouped by certainty.
 * Date-only keys are civil dates and do not use the device timezone.
 */

import type { Booking, TripDocument } from "./schema.ts";
import { bookingLedger, householdSupplierRemaining } from "./ledger.ts";
import { formatDateLabel, paymentDateKey } from "./time.ts";

export type UpcomingGroupKind =
  | "confirmed_deadline"
  | "expected_date"
  | "tentative_date"
  | "pay_at_property"
  | "unscheduled";

export type UpcomingItem = {
  bookingId: string;
  title: string;
  householdId: string | null;
  householdLabel?: string;
  currency: string;
  amountMinor: number;
  date: string | null;
  display: string | null;
  hardDeadline: boolean;
  group: UpcomingGroupKind;
  paid: boolean;
  collectionMethod?: string | null;
};

export function isExtraNightSegment(booking: Booking, doc: TripDocument): boolean {
  if (booking.kind !== "hotel_reservation" || !booking.stayId || !booking.endDate) return false;
  return doc.bookings.some((other) =>
    other.id !== booking.id
    && other.kind === "hotel_reservation"
    && other.stayId === booking.stayId
    && other.householdId === booking.householdId
    && other.startDate
    && booking.endDate
    && other.startDate >= booking.endDate,
  );
}

export function bookingSupplierName(booking: Booking, doc: TripDocument): string {
  const stay = doc.stays.find((s) => s.id === booking.stayId);
  const place = stay ? doc.places.find((p) => p.id === stay.placeId) : undefined;
  if (booking.kind === "hotel_reservation") {
    return place?.name ?? booking.provider ?? booking.title ?? "Hotel";
  }
  if (booking.kind === "excursion") {
    return booking.title ?? booking.provider ?? "Excursion";
  }
  return booking.provider ?? booking.title ?? place?.name ?? booking.kind;
}

export function bookingDisplayTitle(
  booking: Booking,
  doc: TripDocument,
  options?: { includeHousehold?: boolean },
): string {
  const household = options?.includeHousehold === false
    ? undefined
    : doc.households.find((h) => h.id === booking.householdId);
  const extra = isExtraNightSegment(booking, doc);
  const range = booking.startDate && booking.endDate
    ? extra
      ? booking.startDate === booking.endDate
        ? `Extra night · ${formatDateLabel(booking.startDate)}`
        : `Extra night · ${formatDateLabel(booking.startDate)}-${formatDateLabel(booking.endDate)}`
      : booking.startDate === booking.endDate
        ? formatDateLabel(booking.startDate)
        : `${formatDateLabel(booking.startDate)}-${formatDateLabel(booking.endDate)}`
    : extra
      ? "Extra night"
      : booking.startDate
        ? formatDateLabel(booking.startDate)
        : null;
  const parts = [
    bookingSupplierName(booking, doc),
    household?.label,
    range,
    booking.roomName && booking.roomName !== booking.title ? booking.roomName : null,
  ].filter(Boolean);
  return parts.join(" · ") || booking.id;
}

function remainingShare(
  booking: Booking,
  share: { householdId: string; amountMinor: number },
  doc: TripDocument,
): { remainingMinor: number; paid: boolean } {
  const ledger = bookingLedger(
    booking,
    doc.supplierPayments,
    doc.supplierRefunds,
    doc.households.map((h) => h.id),
  );
  const remaining = householdSupplierRemaining(ledger, share.householdId);
  return { remainingMinor: remaining, paid: remaining <= 0 && share.amountMinor > 0 };
}

export function upcomingPayments(doc: TripDocument): UpcomingItem[] {
  const items: UpcomingItem[] = [];
  for (const booking of doc.bookings) {
    if (booking.bookingStatus === "cancelled" || !booking.cost) continue;
    const terms = booking.paymentTerms;
    const group: UpcomingGroupKind = !terms
      ? "unscheduled"
      : terms.kind === "unknown"
        ? "unscheduled"
        : terms.kind;
    const shares = booking.allocations.length > 0
      ? booking.allocations
      : [{ householdId: booking.householdId ?? "", amountMinor: booking.cost.amountMinor }];
    for (const share of shares) {
      const household = doc.households.find((h) => h.id === share.householdId);
      const { remainingMinor, paid } = remainingShare(booking, share, doc);
      items.push({
        bookingId: booking.id,
        title: bookingDisplayTitle(booking, doc, { includeHousehold: false }),
        householdId: share.householdId || null,
        householdLabel: household?.label,
        currency: booking.cost.currency,
        amountMinor: remainingMinor,
        date: terms?.date ? paymentDateKey(terms.date) : null,
        display: terms?.display ?? null,
        hardDeadline: Boolean(terms?.hardDeadline),
        group,
        paid,
        collectionMethod: terms?.collectionMethod ?? null,
      });
    }
  }
  return items;
}

export function isOverdue(item: UpcomingItem, todayDateOnly: string): boolean {
  if (item.paid || !item.date) return false;
  if (item.group === "tentative_date" || !item.hardDeadline) return false;
  return item.date < todayDateOnly;
}

export function groupUpcoming(items: UpcomingItem[]): Record<UpcomingGroupKind, UpcomingItem[]> {
  const groups: Record<UpcomingGroupKind, UpcomingItem[]> = {
    confirmed_deadline: [],
    expected_date: [],
    tentative_date: [],
    pay_at_property: [],
    unscheduled: [],
  };
  for (const item of items) groups[item.group].push(item);
  for (const key of Object.keys(groups) as UpcomingGroupKind[]) {
    groups[key].sort((a, b) => (a.date ?? "9999").localeCompare(b.date ?? "9999"));
  }
  return groups;
}

export function chronologicalSchedule(items: UpcomingItem[]): {
  dated: UpcomingItem[];
  payAtProperty: UpcomingItem[];
  unscheduled: UpcomingItem[];
} {
  const unpaid = items.filter((item) => !item.paid);
  const dated = unpaid
    .filter((item) => item.date && item.group !== "pay_at_property")
    .sort((a, b) => (a.date ?? "").localeCompare(b.date ?? "") || a.title.localeCompare(b.title));
  const payAtProperty = unpaid.filter((item) => item.group === "pay_at_property");
  const unscheduled = unpaid.filter((item) => !item.date && item.group !== "pay_at_property");
  return { dated, payAtProperty, unscheduled };
}

export function dateCertaintyLabel(group: UpcomingGroupKind): string {
  if (group === "confirmed_deadline") return "Deadline";
  if (group === "expected_date") return "Expected";
  if (group === "tentative_date") return "Around this date";
  if (group === "pay_at_property") return "Pay at the property";
  return "Date to confirm";
}
